import { useState } from 'react';
import type { JSX } from 'react';
import {
  Button,
  SettingsMessage,
  SettingsRow,
  SettingsSection,
  peutEcrireSurPorte,
  raisonDeLectureSeule,
  useAccesPort,
  usePersonnesPort,
} from '@taki/ui';
import type { AppDeclaree } from './app-declaree.js';
import { useAppRuntime } from './runtime.js';

/** La demonstration est-elle offerte ici (un jeu declare, et la bonne pile) ? */
export function demoOfferte(app: AppDeclaree, emulateurs: boolean | undefined): boolean {
  return app.demo !== undefined && (!app.demo.local || emulateurs === true);
}

/**
 * LE JEU DE DEMONSTRATION D'UNE APP, dans ses reglages generaux. L'app ne
 * fournit que l'import (`demo` de `definirAppDeclaree`) ; la ligne est la meme
 * pour toutes, faite des briques du gabarit.
 */
export function ReglageDemo({ app }: { app: AppDeclaree }): JSX.Element | null {
  const { sdk, orgId = '', emulateurs } = useAppRuntime();
  const acces = useAccesPort();
  const personnes = usePersonnesPort();
  const [enCours, setEnCours] = useState(false);
  const [retour, setRetour] = useState<{ echec: boolean; message: string } | null>(null);
  const demo = app.demo;
  if (demo === undefined || !demoOfferte(app, emulateurs)) return null;

  const refuse = (demo.ecrit ?? []).find((jeu) => !peutEcrireSurPorte(acces, jeu.family, jeu.type));
  const raison = refuse === undefined ? undefined : (raisonDeLectureSeule(acces, refuse) ?? 'Accès en lecture seule.');

  async function importer(): Promise<void> {
    if (demo === undefined || enCours || raison !== undefined) return;
    setEnCours(true);
    setRetour(null);
    try {
      const moi = personnes?.pret ? (personnes.moi?.personId ?? null) : null;
      setRetour({ echec: false, message: (await demo.importer({ sdk, orgId, moi })).message });
    } catch (cause) {
      setRetour({ echec: true, message: cause instanceof Error ? cause.message : "L'import a échoué." });
    } finally {
      setEnCours(false);
    }
  }

  return (
    <SettingsSection title="Données de démonstration">
      <SettingsRow label="Données d’exemple" {...(raison === undefined ? {} : { lock: raison })}>
        <Button variant="secondary" size="sm" loading={enCours} disabled={raison !== undefined} onClick={() => void importer()}>
          Importer
        </Button>
      </SettingsRow>
      {retour !== null ? (
        <SettingsMessage tone={retour.echec ? 'error' : 'status'} end>
          {retour.message}
        </SettingsMessage>
      ) : null}
    </SettingsSection>
  );
}
